import { cubicOut } from 'svelte/easing';

export const PANEL_SLIDE_DURATION_MS = 180;

interface PanelSlideParameters {
  preserveScroll?: boolean;
}

export interface PanelResizeSnapshot {
  height: number;
  scrollX: number;
  scrollY: number;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function capturePanelResize(node: HTMLElement): PanelResizeSnapshot {
  return {
    height: node.getBoundingClientRect().height,
    scrollX: typeof window === 'undefined' ? 0 : window.scrollX,
    scrollY: typeof window === 'undefined' ? 0 : window.scrollY
  };
}

/** Restores the viewport when a panel reflow above it triggered scroll anchoring. */
export function restoreViewportAfterPanelResize(node: HTMLElement, snapshot: PanelResizeSnapshot): boolean {
  const resized = Math.abs(node.getBoundingClientRect().height - snapshot.height) > 0.5;
  if (!resized || typeof window === 'undefined') return false;
  if (window.scrollX !== snapshot.scrollX || window.scrollY !== snapshot.scrollY) {
    window.scrollTo({ left: snapshot.scrollX, top: snapshot.scrollY, behavior: 'auto' });
  }
  return true;
}

/** Shared transition for panels that are inserted into or removed from the workspace. */
export function panelSlide(node: HTMLElement, parameters: PanelSlideParameters = {}) {
  const scrollX = typeof window === 'undefined' ? 0 : window.scrollX;
  const scrollY = typeof window === 'undefined' ? 0 : window.scrollY;

  function restoreScrollPosition() {
    if (!parameters.preserveScroll || typeof window === 'undefined') return;
    if (window.scrollX === scrollX && window.scrollY === scrollY) return;
    window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
  }

  return {
    duration: prefersReducedMotion() ? 0 : PANEL_SLIDE_DURATION_MS,
    easing: cubicOut,
    tick: (progress: number) => {
      if (progress >= 1) {
        node.style.removeProperty('height');
        node.style.removeProperty('opacity');
        node.style.removeProperty('overflow');
      } else {
        node.style.height = `${Math.max(0, node.scrollHeight * progress)}px`;
        node.style.opacity = String(progress);
        node.style.overflow = 'clip';
      }

      if (parameters.preserveScroll) {
        // Flush the height change before undoing any browser scroll anchoring.
        void node.offsetHeight;
        restoreScrollPosition();

        // Svelte removes an outro node immediately after its final tick. Restore
        // once more after that removal so it cannot move the viewport either.
        if (progress <= 0 || progress >= 1) queueMicrotask(restoreScrollPosition);
      }
    }
  };
}
